import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { invalidateMaster, COLL } from '../../utils/masterDataStore';
import { isValidEmail, isValidPhone } from '../../utils/validationHelpers';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  FiPlus, FiEdit2, FiTrash2, FiUsers, FiSearch, FiX,
  FiCheck, FiPhone, FiMail, FiDollarSign,
  FiMoreVertical, FiBookOpen, FiTrendingUp, FiFileText, FiCreditCard
} from 'react-icons/fi';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';

const Employees = () => {
  const { activeCompany } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'employees');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('All');
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingEmployee, setEditingEmployee] = useState(null);

  // Actions menu (portal)
  const navigate = useNavigate();
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [actionMenuPos, setActionMenuPos] = useState({ top: 0, left: 0 });
  const closeActionMenu = () => setOpenActionMenuId(null);
  const toggleActionMenu = (employeeId, e) => {
    if (openActionMenuId === employeeId) { closeActionMenu(); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuWidth = 240;
    const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8);
    setActionMenuPos({ top: rect.bottom + 4, left: Math.max(8, left) });
    setOpenActionMenuId(employeeId);
  };
  useEffect(() => {
    if (!openActionMenuId) return;
    const close = (e) => {
      if (!e.target.closest('[data-action-menu]') && !e.target.closest('[data-action-trigger]')) closeActionMenu();
    };
    const onScroll = () => closeActionMenu();
    document.addEventListener('mousedown', close);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [openActionMenuId]);

  const handleOpenSalaryRegister = (emp) => {
    closeActionMenu();
    navigate('/reports/salary-register', { state: { employeeId: emp.id } });
  };
  const handleOpenSalesmanPerformance = (emp) => {
    closeActionMenu();
    navigate('/reports/salesperson-performance', { state: { employeeId: emp.id } });
  };
  const handleOpenAdvance = (emp) => {
    closeActionMenu();
    navigate('/salary/advance', { state: { prefill: { employeeId: emp.id, employeeName: emp.fullName || `${emp.firstName} ${emp.lastName}`.trim() } } });
  };
  const handleOpenLoan = (emp) => {
    closeActionMenu();
    navigate('/salary/loan', { state: { prefill: { employeeId: emp.id, employeeName: emp.fullName || `${emp.firstName} ${emp.lastName}`.trim() } } });
  };
  const [formData, setFormData] = useState({
    employeeCode: '',
    firstName: '',
    lastName: '',
    cnic: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    department: 'Sales',
    designation: '',
    joiningDate: '',
    salary: 0,
    isSalesman: false,
    commissionType: 'Percentage',
    commissionRate: 0,
    targetAmount: 0,
    isActive: true
  });

  useEffect(() => {
    if (activeCompany?.id) {
      fetchEmployees();
    }
  }, [activeCompany]);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, `companies/${activeCompany.id}/employees`),
        orderBy('employeeCode')
      );
      const snapshot = await getDocs(q);
      setEmployees(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
    setLoading(false);
  };

  const generateEmployeeCode = () => {
    const maxCode = employees.reduce((max, emp) => {
      const num = parseInt(emp.employeeCode.replace('EMP-', '')) || 0;
      return num > max ? num : max;
    }, 0);
    return `EMP-${String(maxCode + 1).padStart(4, '0')}`;
  };

  const handleAdd = () => {
    setFormData({
      employeeCode: generateEmployeeCode(),
      firstName: '',
      lastName: '',
      cnic: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      department: 'Sales',
      designation: '',
      joiningDate: new Date().toISOString().split('T')[0],
      salary: 0,
      isSalesman: false,
      commissionType: 'Percentage',
      commissionRate: 0,
      targetAmount: 0,
      isActive: true
    });
    setEditingEmployee(null);
    setShowModal(true);
  };

  const handleEdit = (employee) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setFormData({
      employeeCode: employee.employeeCode,
      firstName: employee.firstName,
      lastName: employee.lastName || '',
      cnic: employee.cnic || '',
      email: employee.email || '',
      phone: employee.phone || '',
      address: employee.address || '',
      city: employee.city || '',
      department: employee.department || 'Sales',
      designation: employee.designation || '',
      joiningDate: employee.joiningDate || '',
      salary: employee.salary || 0,
      isSalesman: employee.isSalesman || false,
      commissionType: employee.commissionType || 'Percentage',
      commissionRate: employee.commissionRate || 0,
      targetAmount: employee.targetAmount || 0,
      isActive: employee.isActive !== false
    });
    setEditingEmployee(employee);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.firstName.trim()) {
      alert('First name is required');
      return;
    }
    if (formData.email && !isValidEmail(formData.email)) {
      alert('Please enter a valid email address');
      return;
    }
    if (formData.phone && !isValidPhone(formData.phone)) {
      alert('Please enter a valid phone number');
      return;
    }

    try {
      const employeeData = {
        ...formData,
        fullName: `${formData.firstName} ${formData.lastName}`.trim(),
        salary: parseFloat(formData.salary) || 0,
        commissionRate: parseFloat(formData.commissionRate) || 0,
        targetAmount: parseFloat(formData.targetAmount) || 0
      };

      if (editingEmployee) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/employees`, editingEmployee.id), {
          ...employeeData,
          updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, `companies/${activeCompany.id}/employees`), {
          ...employeeData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }
      setShowModal(false);
      // Employees are cached for the transaction forms — push the change out
      // now, otherwise other screens keep the old list until the next refresh.
      invalidateMaster(activeCompany.id, COLL.employees);
      fetchEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Failed to save employee');
    }
  };

  // Keyboard shortcuts: Ctrl+N to open add modal, Ctrl+S to save
  useDocumentShortcuts({
    onNew: handleAdd,
    onSave: handleSave,
    viewMode: showModal ? 'form' : 'list',
    saving: false
  });

  const handleDelete = async (employee) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (window.confirm(`Are you sure you want to delete "${employee.fullName || employee.firstName}"?`)) {
      try {
        const { hasReferences, message } = await checkEntityReferences(activeCompany?.id, 'employee', employee.id);
        if (hasReferences) { alert(message); return; }
        await deleteDoc(doc(db, `companies/${activeCompany.id}/employees`, employee.id));
        invalidateMaster(activeCompany.id, COLL.employees);
        fetchEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        alert('Failed to delete employee');
      }
    }
  };

  const toggleActive = async (employee) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    try {
      await updateDoc(doc(db, `companies/${activeCompany.id}/employees`, employee.id), {
        isActive: !employee.isActive,
        updatedAt: serverTimestamp()
      });
      invalidateMaster(activeCompany.id, COLL.employees);
      fetchEmployees();
    } catch (error) {
      console.error('Error toggling active status:', error);
    }
  };

  const departments = ['Sales', 'Accounts', 'Purchase', 'Warehouse', 'Admin', 'HR', 'IT', 'Production', 'Marketing', 'Logistics'];

  const filteredEmployees = employees.filter(emp => {
    const matchesSearch =
      (emp.fullName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employeeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.phone || '').includes(searchTerm);

    const matchesDepartment = filterDepartment === 'All' || emp.department === filterDepartment;

    return matchesSearch && matchesDepartment;
  });

  const activeEmployees = employees.filter(emp => emp.isActive !== false);
  const salesPersons = employees.filter(emp => emp.isSalesman);
  const uniqueDepartments = [...new Set(employees.map(emp => emp.department))];

  return (
    <PageLayout pageTitle="Employees" breadcrumb="Master Data / Employees">
      <Container>
        <HeaderSection>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')}>
                <FiX />
              </ClearButton>
            )}
          </SearchBox>
          <FilterSelect
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
          >
            <option value="All">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </FilterSelect>
          {canEdit && (
            <AddButton onClick={handleAdd}>
              <FiPlus /> Add Employee
            </AddButton>
          )}
        </HeaderSection>

        <StatsRow>
          <StatCard>
            <StatIcon $color={colors.primary.main}><FiUsers /></StatIcon>
            <StatInfo>
              <StatValue>{employees.length}</StatValue>
              <StatLabel>Total Employees</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.success.main}><FiCheck /></StatIcon>
            <StatInfo>
              <StatValue>{activeEmployees.length}</StatValue>
              <StatLabel>Active</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.info.main}><FiDollarSign /></StatIcon>
            <StatInfo>
              <StatValue>{salesPersons.length}</StatValue>
              <StatLabel>Sales Persons</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.warning.main}><FiUsers /></StatIcon>
            <StatInfo>
              <StatValue>{uniqueDepartments.length}</StatValue>
              <StatLabel>Departments</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsRow>

        {loading ? (
          <LoadingMessage>Loading employees...</LoadingMessage>
        ) : (
          <TableContainer>
            {filteredEmployees.length === 0 ? (
              <EmptyMessage>
                {searchTerm || filterDepartment !== 'All'
                  ? 'No employees found matching your criteria.'
                  : 'No employees found. Click "Add Employee" to create one.'}
              </EmptyMessage>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Department</th>
                    <th>Designation</th>
                    <th>Contact</th>
                    <th>Salesman</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.map(employee => (
                    <tr key={employee.id}>
                      <td>
                        <EmployeeCode>{employee.employeeCode}</EmployeeCode>
                      </td>
                      <td>
                        <EmployeeName>
                          <span>{employee.fullName || employee.firstName}</span>
                          {employee.cnic && <small>CNIC: {employee.cnic}</small>}
                        </EmployeeName>
                      </td>
                      <td>
                        <DepartmentBadge $dept={employee.department}>
                          {employee.department}
                        </DepartmentBadge>
                      </td>
                      <td>{employee.designation || '-'}</td>
                      <td>
                        <ContactInfo>
                          {employee.phone && (
                            <ContactItem>
                              <FiPhone /> {employee.phone}
                            </ContactItem>
                          )}
                          {employee.email && (
                            <ContactItem>
                              <FiMail /> {employee.email}
                            </ContactItem>
                          )}
                          {!employee.phone && !employee.email && '-'}
                        </ContactInfo>
                      </td>
                      <td>
                        {employee.isSalesman ? (
                          <SalesmanBadge>
                            Sales Person
                            <small>
                              {employee.commissionType === 'Percentage'
                                ? `${employee.commissionRate}%`
                                : `${activeCompany?.currencySymbol || 'Rs.'} ${employee.commissionRate?.toLocaleString()}`}
                            </small>
                          </SalesmanBadge>
                        ) : (
                          <span style={{ color: colors.text.secondary }}>-</span>
                        )}
                      </td>
                      <td>
                        <StatusToggle
                          $active={employee.isActive !== false}
                          onClick={canEdit ? () => toggleActive(employee) : undefined}
                          style={canEdit ? undefined : { cursor: 'default' }}
                        >
                          {employee.isActive !== false ? 'Active' : 'Inactive'}
                        </StatusToggle>
                      </td>
                      <td>
                        <ActionButtons>
                          <ActionButton
                            data-action-trigger
                            onClick={(e) => toggleActionMenu(employee.id, e)}
                            title="Actions"
                          >
                            <FiMoreVertical />
                          </ActionButton>
                        </ActionButtons>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>
        )}
      </Container>

      {/* Actions menu (portal) */}
      {openActionMenuId && (() => {
        const emp = employees.find(e => e.id === openActionMenuId);
        if (!emp) return null;
        return createPortal(
          <ActionDropdown
            data-action-menu
            style={{ top: actionMenuPos.top, left: actionMenuPos.left }}
          >
            {canChange && (
              <ActionDropItem onClick={() => { closeActionMenu(); handleEdit(emp); }}>
                <FiEdit2 size={14} /> Edit
              </ActionDropItem>
            )}
            <ActionDropItem onClick={() => handleOpenSalaryRegister(emp)}>
              <FiBookOpen size={14} /> Salary Register
            </ActionDropItem>
            {emp.isSalesman && (
              <ActionDropItem onClick={() => handleOpenSalesmanPerformance(emp)}>
                <FiTrendingUp size={14} /> Salesman Performance
              </ActionDropItem>
            )}
            <ActionDropItem onClick={() => handleOpenAdvance(emp)}>
              <FiCreditCard size={14} /> New Salary Advance
            </ActionDropItem>
            <ActionDropItem onClick={() => handleOpenLoan(emp)}>
              <FiFileText size={14} /> New Staff Loan
            </ActionDropItem>
            {canDelete && (
              <>
                <ActionDropDivider />
                <ActionDropItem $danger onClick={() => { closeActionMenu(); handleDelete(emp); }}>
                  <FiTrash2 size={14} /> Delete
                </ActionDropItem>
              </>
            )}
          </ActionDropdown>,
          document.body
        );
      })()}

      {/* Add/Edit Modal */}
      {showModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</h3>
              <CloseButton onClick={() => setShowModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormSection>
                <SectionTitle>Basic Information</SectionTitle>
                <FormRow>
                  <FormGroup>
                    <label>Employee Code</label>
                    <input
                      type="text"
                      value={formData.employeeCode}
                      onChange={(e) => setFormData({ ...formData, employeeCode: e.target.value })}
                      disabled={editingEmployee}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>CNIC</label>
                    <input
                      type="text"
                      value={formData.cnic}
                      onChange={(e) => setFormData({ ...formData, cnic: e.target.value })}
                      placeholder="00000-0000000-0"
                    />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <label>First Name *</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      placeholder="Enter first name"
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Enter last name"
                    />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <label>Phone</label>
                    <input
                      type="text"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Enter phone number"
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email"
                    />
                  </FormGroup>
                </FormRow>
                <FormGroup>
                  <label>Address</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Enter address"
                    rows={2}
                  />
                </FormGroup>
                <FormGroup>
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Enter city"
                  />
                </FormGroup>
              </FormSection>

              <FormSection>
                <SectionTitle>Employment Details</SectionTitle>
                <FormRow>
                  <FormGroup>
                    <label>Department</label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    >
                      {departments.map(dept => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </FormGroup>
                  <FormGroup>
                    <label>Designation</label>
                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      placeholder="Enter designation"
                    />
                  </FormGroup>
                </FormRow>
                <FormRow>
                  <FormGroup>
                    <label>Joining Date</label>
                    <input
                      type="date"
                      value={formData.joiningDate}
                      onChange={(e) => setFormData({ ...formData, joiningDate: e.target.value })}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Salary</label>
                    <input
                      type="number"
                      value={formData.salary}
                      onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                      placeholder="Enter salary"
                      min="0"
                    />
                  </FormGroup>
                </FormRow>
              </FormSection>

              <FormSection>
                <SectionTitle>Sales Person Settings</SectionTitle>
                <FormGroup>
                  <CheckboxGroup>
                    <input
                      type="checkbox"
                      id="isSalesman"
                      checked={formData.isSalesman}
                      onChange={(e) => setFormData({ ...formData, isSalesman: e.target.checked })}
                    />
                    <label htmlFor="isSalesman">Is a Sales Person</label>
                  </CheckboxGroup>
                </FormGroup>
                {formData.isSalesman && (
                  <>
                    <FormRow>
                      <FormGroup>
                        <label>Commission Type</label>
                        <select
                          value={formData.commissionType}
                          onChange={(e) => setFormData({ ...formData, commissionType: e.target.value })}
                        >
                          <option value="Percentage">Percentage (%)</option>
                          <option value="Fixed">Fixed Amount ({activeCompany?.currencySymbol || 'Rs.'})</option>
                        </select>
                      </FormGroup>
                      <FormGroup>
                        <label>Commission Rate</label>
                        <input
                          type="number"
                          value={formData.commissionRate}
                          onChange={(e) => setFormData({ ...formData, commissionRate: e.target.value })}
                          placeholder={formData.commissionType === 'Percentage' ? 'Enter %' : 'Enter amount'}
                          min="0"
                          step={formData.commissionType === 'Percentage' ? '0.1' : '1'}
                        />
                      </FormGroup>
                    </FormRow>
                    <FormGroup>
                      <label>Monthly Target Amount</label>
                      <input
                        type="number"
                        value={formData.targetAmount}
                        onChange={(e) => setFormData({ ...formData, targetAmount: e.target.value })}
                        placeholder="Enter target amount"
                        min="0"
                      />
                    </FormGroup>
                  </>
                )}
              </FormSection>

              <FormGroup>
                <CheckboxGroup>
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <label htmlFor="isActive">Active Employee</label>
                </CheckboxGroup>
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSave}>
                  {editingEmployee ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  flex: 1;
  max-width: 300px;

  svg {
    color: ${colors.text.secondary};
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: ${typography.fontSize.sm};
    background: transparent;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.hover};
  }
`;

const FilterSelect = styled.select`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  background: ${colors.background.default};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing.md};
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: ${colors.background.default};
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: ${typography.fontSize.h4};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;

const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const TableContainer = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th, td {
    padding: ${spacing.md};
    text-align: left;
    border-bottom: 1px solid ${colors.border.light};
  }

  th {
    background: ${colors.background.paper};
    font-size: ${typography.fontSize.xs};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  td {
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
  }

  tbody tr:hover {
    background: ${colors.background.hover};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const EmployeeCode = styled.span`
  font-family: monospace;
  font-size: ${typography.fontSize.xs};
  background: ${colors.background.paper};
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary};
`;

const EmployeeName = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  span {
    font-weight: ${typography.fontWeight.medium};
  }

  small {
    font-size: ${typography.fontSize.xs};
    color: ${colors.text.secondary};
  }
`;

const DepartmentBadge = styled.span`
  font-size: ${typography.fontSize.xs};
  padding: 4px 10px;
  border-radius: ${borderRadius.sm};
  background: ${props => {
    switch (props.$dept) {
      case 'Sales': return colors.success.main + '20';
      case 'Accounts': return colors.info.main + '20';
      case 'Purchase': return colors.warning.main + '20';
      case 'Warehouse': return colors.secondary.main + '20';
      case 'Admin': return colors.primary.main + '20';
      case 'HR': return colors.error.main + '20';
      default: return colors.text.secondary + '20';
    }
  }};
  color: ${props => {
    switch (props.$dept) {
      case 'Sales': return colors.success.main;
      case 'Accounts': return colors.info.main;
      case 'Purchase': return colors.warning.main;
      case 'Warehouse': return colors.secondary.main;
      case 'Admin': return colors.primary.main;
      case 'HR': return colors.error.main;
      default: return colors.text.secondary;
    }
  }};
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};

  svg {
    font-size: 12px;
  }
`;

const SalesmanBadge = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: ${typography.fontSize.xs};
  background: ${colors.success.main}15;
  color: ${colors.success.main};
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  font-weight: ${typography.fontWeight.medium};

  small {
    font-weight: ${typography.fontWeight.regular};
    opacity: 0.8;
  }
`;

const StatusToggle = styled.button`
  font-size: ${typography.fontSize.xs};
  padding: 4px 12px;
  border-radius: ${borderRadius.full};
  background: ${props => props.$active ? colors.success.main + '20' : colors.error.main + '20'};
  color: ${props => props.$active ? colors.success.main : colors.error.main};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$active ? colors.success.main + '30' : colors.error.main + '30'};
  }
`;

const ActionDropdown = styled.div`
  position: fixed;
  min-width: 240px;
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.18);
  z-index: 9999;
  padding: 4px;
  overflow: hidden;
`;
const ActionDropItem = styled.button`
  display: flex; align-items: center; gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: none; border: none; text-align: left;
  font-size: 13px; cursor: pointer;
  color: ${p => p.$danger ? colors.error.main : colors.text.primary};
  border-radius: 6px;
  &:hover { background: ${p => p.$danger ? `${colors.error.main}10` : colors.neutral[50]}; }
  svg { flex-shrink: 0; opacity: 0.75; }
`;
const ActionDropDivider = styled.div`
  height: 1px; background: ${colors.neutral[200]}; margin: 4px 0;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${props => props.$danger ? colors.error.main : colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '15' : colors.background.hover};
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.md};
`;

const Modal = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};
  position: sticky;
  top: 0;
  background: ${colors.background.default};
  z-index: 1;

  h3 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const SectionTitle = styled.h4`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  padding-bottom: ${spacing.xs};
  border-bottom: 1px solid ${colors.border.light};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  label {
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.secondary};
  }

  input, select, textarea {
    padding: ${spacing.sm} ${spacing.md};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
    background: ${colors.background.default};
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
      box-shadow: 0 0 0 3px ${colors.primary.main}20;
    }

    &:disabled {
      background: ${colors.background.paper};
      cursor: not-allowed;
    }
  }

  textarea {
    resize: vertical;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }

  label {
    cursor: pointer;
    font-weight: ${typography.fontWeight.regular};
    color: ${colors.text.primary};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.lg};
  border-top: 1px solid ${colors.border.light};
  position: sticky;
  bottom: 0;
  background: ${colors.background.default};
`;

const CancelButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: transparent;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    border-color: ${colors.text.secondary};
  }
`;

const SaveButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

export default Employees;
